import { createRoute } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input } from '@arabquiz/ui'
import {
  Clock,
  FilePlus2,
  FileQuestion,
  Loader2,
  Play,
  Plus,
  Search,
} from 'lucide-react'
import { Route as appRoute } from '../../_app'

/**
 * Quiz list page — the host's "my quizzes" dashboard.
 *
 * Fetches `GET /api/v1/quizzes` once via TanStack Query (the API is already
 * tenant-scoped server-side by the T20 query filter, so the client receives
 * only the caller's organization's quizzes — no further filtering needed
 * for isolation). The search input is a pure client-side projection of the
 * loaded list: title-substring, accent-insensitive, no debounce because we
 * never round-trip the API on keystrokes.
 *
 * Three render branches: skeleton during the initial fetch, a centered
 * error card on failure, and the list itself (or the empty / no-results
 * placeholders) on success. Each card has a primary edit link (title /
 * description area) and a secondary "host this quiz" button that POSTs
 * `/api/v1/games` and jumps to `/games/host/{gameId}` on success — a
 * shortcut from the list surface that mirrors what the `/dashboard` page
 * does, so power users don't have to bounce through a separate screen.
 *
 * Visual direction: editorial confidence — a Tajawal display headline with
 * a brand accent rule, an oversized count, a rounded-full search input, and
 * a responsive card grid where each card carries an `border-s-4` brand
 * stripe on the leading edge (start-aware so it lives on the visual right
 * under RTL). All spacing utilities are logical (`ps-*`, `pe-*`, `me-*`,
 * `space-y-*`) so the layout reads correctly without overrides.
 */
export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/quizzes/',
  component: QuizzesIndexPage,
})

type QuizSummary = {
  id: string
  title: string
  description: string
  questionCount: number
  updatedAt: string
}

type CreateGameResponse = {
  id: string
  pin: string
}

function QuizzesIndexPage() {
  const { t } = useTranslation('quiz')
  const { t: tCommon } = useTranslation('common')
  const [search, setSearch] = useState('')
  // Track the quiz whose "Host" button is in-flight so we can swap the
  // affordance for a spinner without disabling sibling cards.
  const [pendingQuizId, setPendingQuizId] = useState<string | null>(null)

  const quizzesQuery = useQuery({
    queryKey: ['quizzes'],
    queryFn: async ({ signal }) => {
      const res = await fetch('/api/v1/quizzes', {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load quizzes: ${res.status}`)
      }
      return (await res.json()) as QuizSummary[]
    },
  })

  const createGameMutation = useMutation({
    mutationFn: async (quizId: string) => {
      const res = await fetch('/api/v1/games', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quizId }),
      })
      if (!res.ok) {
        throw new Error(`Failed to create game: ${res.status}`)
      }
      return (await res.json()) as CreateGameResponse
    },
    onSuccess: (game) => {
      // Full-page navigation: the host route was added in the same task
      // and TanStack Router's tree may not have it indexed yet at this
      // file's typecheck. The next load resolves the regenerated tree.
      window.location.assign(`/games/host/${encodeURIComponent(game.id)}`)
    },
    onError: () => {
      setPendingQuizId(null)
    },
  })

  const handleHost = (quizId: string) => {
    setPendingQuizId(quizId)
    createGameMutation.mutate(quizId)
  }

  // Pre-compute the normalized needle once per keystroke and run a simple
  // includes() check; the dataset is bounded (one host's quizzes) so a
  // linear scan beats setting up Fuse.js or worker threads.
  const filteredQuizzes = useMemo(() => {
    const all = quizzesQuery.data ?? []
    const needle = normalizeArabic(search.trim())
    if (!needle) return all
    return all.filter((q) => normalizeArabic(q.title).includes(needle))
  }, [quizzesQuery.data, search])

  return (
    <main className="min-h-screen bg-gradient-to-bl from-brand-50/40 via-white to-white">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-10">
          <div
            className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
            aria-hidden="true"
          />
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold font-display text-brand-900 tracking-tight">
                {t('myQuizzes')}
              </h1>
              {quizzesQuery.isSuccess ? (
                <p className="mt-3 text-brand-700 text-base">
                  {t('quizzesCount', { count: quizzesQuery.data.length })}
                </p>
              ) : null}
            </div>

            <a href="/quizzes/new" className="inline-block">
              <Button size="lg" className="gap-2">
                <Plus className="h-5 w-5" aria-hidden="true" />
                <span>{t('createQuiz')}</span>
              </Button>
            </a>
          </div>
        </header>

        <div className="relative mb-8 max-w-xl">
          <Search
            className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-400"
            aria-hidden="true"
          />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="ps-10 rounded-full h-11"
            aria-label={t('searchPlaceholder')}
          />
        </div>

        {createGameMutation.isError ? (
          <div
            className="mb-6 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3"
            role="alert"
          >
            {t('hostError')}
          </div>
        ) : null}

        {quizzesQuery.isLoading ? <QuizListSkeleton /> : null}

        {quizzesQuery.isError ? (
          <div
            className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3"
            role="alert"
          >
            {t('loadError')}
          </div>
        ) : null}

        {quizzesQuery.isSuccess && quizzesQuery.data.length === 0 ? (
          <EmptyState
            title={t('noQuizzesYet')}
            description={t('noQuizzesYetDescription')}
            ctaLabel={t('createQuiz')}
          />
        ) : null}

        {quizzesQuery.isSuccess && quizzesQuery.data.length > 0 ? (
          filteredQuizzes.length === 0 ? (
            <p className="text-center text-brand-700 py-12 text-lg">
              {t('noSearchResults')}
            </p>
          ) : (
            <ul
              role="list"
              className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5"
            >
              {filteredQuizzes.map((quiz) => (
                <li key={quiz.id}>
                  <QuizCard
                    quiz={quiz}
                    onHost={() => handleHost(quiz.id)}
                    isHostPending={
                      pendingQuizId === quiz.id && createGameMutation.isPending
                    }
                    questionCountLabel={t('questionsCount', {
                      count: quiz.questionCount,
                    })}
                    lastUpdatedLabel={t('lastUpdated')}
                    hostLabel={t('hostGame')}
                    hostAriaLabel={t('hostGameAria')}
                    preparingLabel={t('preparingGame')}
                  />
                </li>
              ))}
            </ul>
          )
        ) : null}

        {quizzesQuery.isLoading ? (
          <span className="sr-only" role="status" aria-live="polite">
            {tCommon('loading')}
          </span>
        ) : null}
      </div>
    </main>
  )
}

type QuizCardProps = {
  quiz: QuizSummary
  onHost: () => void
  isHostPending: boolean
  questionCountLabel: string
  lastUpdatedLabel: string
  hostLabel: string
  hostAriaLabel: string
  preparingLabel: string
}

/**
 * Card layout: the title / description area is the editor link, and a
 * dedicated "host this quiz" button sits in the footer. We deliberately
 * avoid the older "click the whole card to edit" pattern because nesting
 * a button inside an `<a>` is invalid markup and produces ambiguous
 * keyboard semantics — splitting the affordances into two distinct
 * interactive zones is both more accessible and more discoverable.
 *
 * The Host button is disabled when the quiz has zero questions; firing
 * a SignalR game with no content is a guaranteed dead-end.
 */
function QuizCard({
  quiz,
  onHost,
  isHostPending,
  questionCountLabel,
  lastUpdatedLabel,
  hostLabel,
  hostAriaLabel,
  preparingLabel,
}: QuizCardProps) {
  const updatedAtDate = new Date(quiz.updatedAt)
  return (
    <article className="group relative flex h-full flex-col rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-500 ps-5 pe-5 py-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:border-brand-200 hover:border-s-brand-600">
      <a
        href={`/quizzes/${encodeURIComponent(quiz.id)}/edit`}
        className="block rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        aria-label={quiz.title}
      >
        <h2 className="text-lg font-bold font-display text-brand-900 tracking-tight line-clamp-2 transition-colors group-hover:text-brand-700">
          {quiz.title}
        </h2>
        {quiz.description ? (
          <p className="mt-2 text-sm text-brand-700 line-clamp-2 leading-relaxed">
            {quiz.description}
          </p>
        ) : null}
      </a>

      <div className="mt-5 flex items-center justify-between gap-2 text-xs text-brand-600">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 font-semibold text-brand-700">
          <FileQuestion className="h-3.5 w-3.5" aria-hidden="true" />
          {questionCountLabel}
        </span>
        <span
          className="inline-flex items-center gap-1.5"
          title={updatedAtDate.toLocaleString('ar')}
        >
          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="sr-only">{lastUpdatedLabel}: </span>
          <time dateTime={quiz.updatedAt}>{formatRelativeAr(updatedAtDate)}</time>
        </span>
      </div>

      <Button
        type="button"
        size="default"
        onClick={onHost}
        disabled={isHostPending || quiz.questionCount === 0}
        aria-label={hostAriaLabel}
        className="mt-4 w-full gap-2"
      >
        {isHostPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span>{preparingLabel}</span>
          </>
        ) : (
          <>
            <Play className="h-4 w-4 fill-current" aria-hidden="true" />
            <span>{hostLabel}</span>
          </>
        )}
      </Button>
    </article>
  )
}

function QuizListSkeleton() {
  return (
    <ul
      role="list"
      aria-hidden="true"
      className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <li
          key={i}
          className="h-52 rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-100 ps-5 pe-5 py-5 animate-pulse"
        >
          <div className="h-5 w-3/4 rounded bg-brand-100" />
          <div className="mt-3 h-3.5 w-full rounded bg-brand-50" />
          <div className="mt-2 h-3.5 w-5/6 rounded bg-brand-50" />
          <div className="mt-6 flex items-center justify-between">
            <div className="h-6 w-20 rounded-full bg-brand-50" />
            <div className="h-3.5 w-16 rounded bg-brand-50" />
          </div>
          <div className="mt-4 h-10 w-full rounded-md bg-brand-100" />
        </li>
      ))}
    </ul>
  )
}

type EmptyStateProps = {
  title: string
  description: string
  ctaLabel: string
}

function EmptyState({ title, description, ctaLabel }: EmptyStateProps) {
  return (
    <div className="rounded-3xl border border-dashed border-brand-200 bg-white/60 px-6 py-16 text-center">
      <div
        className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 ring-1 ring-brand-200/60"
        aria-hidden="true"
      >
        <FilePlus2 className="h-10 w-10 text-brand-600" strokeWidth={1.5} />
      </div>
      <h2 className="text-2xl font-bold font-display text-brand-900 tracking-tight mb-2">
        {title}
      </h2>
      <p className="text-brand-700 mb-6 max-w-md mx-auto leading-relaxed">
        {description}
      </p>
      <a href="/quizzes/new" className="inline-block">
        <Button size="lg" className="gap-2">
          <Plus className="h-5 w-5" aria-hidden="true" />
          <span>{ctaLabel}</span>
        </Button>
      </a>
    </div>
  )
}

/**
 * Normalize an Arabic string for accent-insensitive search:
 *   - strip tashkeel (vowel marks) so searching "كتاب" matches "كِتاب"
 *   - fold alef variants (أ إ آ → ا) and final yaa (ى → ي)
 *   - fold taa marbuta (ة → ه) so name vs. noun forms match
 *   - lowercase the Latin portion in case quiz titles mix scripts.
 *
 * This is intentionally conservative; we never strip the underlying
 * characters from the source data, only from the search projection.
 */
function normalizeArabic(input: string): string {
  return input
    .toLowerCase()
    .replace(/[\u064B-\u0652]/g, '')
    .replace(/[\u0623\u0625\u0622]/g, '\u0627')
    .replace(/\u0649/g, '\u064A')
    .replace(/\u0629/g, '\u0647')
}

/**
 * Format a date as an Arabic relative time ("منذ ٣ أيام", "منذ ساعة") using
 * `Intl.RelativeTimeFormat`. Falls back to an absolute `yyyy/mm/dd` date
 * (Western digits, no directional marks) for anything older than a year to
 * avoid awkward phrasings.
 */
function formatRelativeAr(date: Date): string {
  const diffMs = date.getTime() - Date.now()
  const diffMin = Math.round(diffMs / 60_000)
  const rtf = new Intl.RelativeTimeFormat('ar', { numeric: 'auto' })

  const absMin = Math.abs(diffMin)
  if (absMin < 1) return rtf.format(0, 'minute')
  if (absMin < 60) return rtf.format(diffMin, 'minute')

  const diffHour = Math.round(diffMs / 3_600_000)
  if (Math.abs(diffHour) < 24) return rtf.format(diffHour, 'hour')

  const diffDay = Math.round(diffMs / 86_400_000)
  if (Math.abs(diffDay) < 30) return rtf.format(diffDay, 'day')

  const diffMonth = Math.round(diffDay / 30)
  if (Math.abs(diffMonth) < 12) return rtf.format(diffMonth, 'month')

  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${y}/${m}/${day}`
}
